import type { ReactNode } from 'react';
import Breadcrumbs, { type BreadcrumbItem } from '../Breadcrumbs';
import CurveDivider from './CurveDivider';

/**
 * C3 title band for long-tail pages: soft grey band with breadcrumb, the H1
 * (weight 300, left-aligned in the centred container), optional eyebrow and
 * lead, ending in the signature curve. Under 260px tall on desktop.
 *
 *   <TitleBand title="Back pain" eyebrow="We can help" breadcrumbs={[{ name: 'Back pain' }]} lead="..." />
 */
export interface TitleBandProps {
  title: ReactNode;
  eyebrow?: ReactNode;
  lead?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  /** Extra content under the lead (chips, a byline). */
  children?: ReactNode;
  /** No title: a thin band + curve so legacy pages that render their own H1 keep the edge. */
  slim?: boolean;
  titleId?: string;
  className?: string;
}

export default function TitleBand({ title, eyebrow, lead, breadcrumbs, children, slim = false, titleId, className = '' }: TitleBandProps) {
  const cls = ['ve-band', slim ? 've-band--slim' : '', className].filter(Boolean).join(' ');
  // A titled band is the page header: a <section> named by its H1 is a region
  // landmark, so the H1, eyebrow and lead are reachable by landmark navigation
  // (the photo hero does the same). The slim band has no content, so it stays a div.
  const Tag = slim || !titleId ? 'div' : 'section';
  return (
    <Tag className={cls} aria-labelledby={Tag === 'section' ? titleId : undefined}>
      {!slim && (
        <div className="ve-container">
          {breadcrumbs && breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} />}
          {eyebrow && <span className="ve-eyebrow">{eyebrow}</span>}
          <h1 className="ve-band-title" id={titleId}>
            {title}
          </h1>
          {lead && <p className="ve-band-lead">{lead}</p>}
          {children}
        </div>
      )}
      <CurveDivider />
    </Tag>
  );
}
